import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../ui/button';
import { useToast } from '../../../../hooks/use-toast';
import { RealtimeChat } from '../../../../utils/RealtimeAudio';
import { Mic, MicOff, Phone, PhoneOff, Loader2, CalendarCheck } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export function VoiceInterface() {
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessingReservation, setIsProcessingReservation] = useState(false);
  const [reservationConfirmed, setReservationConfirmed] = useState(false);
  const [status, setStatus] = useState<'ready' | 'connecting' | 'listening' | 'speaking' | 'processing'>('ready');
  const [messages, setMessages] = useState<Message[]>([]);
  const chatRef = useRef<RealtimeChat | null>(null);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMessage = (event: Record<string, unknown>) => {
    console.log('Received message:', event);

    if (event.type === 'response.audio.delta') {
      setIsSpeaking(true);
      setStatus('speaking');
    } else if (event.type === 'response.audio.done') {
      setIsSpeaking(false);
      setStatus(isProcessingReservation ? 'processing' : 'listening');
    } else if (event.type === 'input_audio_buffer.speech_started') {
      setStatus('listening');
    } else if (event.type === 'response.audio_transcript.done') {
      const transcript = (event as { transcript?: string }).transcript;
      if (transcript) {
        setMessages(prev => [...prev, { role: 'assistant', content: transcript }]);
        
        // Detect when processing a reservation
        const processingPhrases = [
          'give me a moment',
          'just a moment',
          'let me book',
          'let me set that up',
          'booking your reservation',
          'processing your reservation',
          'setting that up'
        ];
        const lowerTranscript = transcript.toLowerCase();
        if (processingPhrases.some(phrase => lowerTranscript.includes(phrase))) {
          setIsProcessingReservation(true);
          setStatus('processing');
          // Auto-clear after 15 seconds if no confirmation comes
          if (processingTimeoutRef.current) clearTimeout(processingTimeoutRef.current);
          processingTimeoutRef.current = setTimeout(() => {
            setIsProcessingReservation(false);
            setStatus('listening');
          }, 15000);
        }
        
        // Detect when reservation is confirmed
        const confirmationPhrases = [
          'reservation confirmed',
          'reservation is confirmed',
          'booked for',
          'all set',
          'confirmation email'
        ];
        if (confirmationPhrases.some(phrase => lowerTranscript.includes(phrase))) {
          setIsProcessingReservation(false);
          setReservationConfirmed(true);
          setStatus('listening');
          if (processingTimeoutRef.current) clearTimeout(processingTimeoutRef.current);
          // Show success briefly
          setTimeout(() => setReservationConfirmed(false), 5000);
        }
      }
    } else if (event.type === 'conversation.item.input_audio_transcription.completed') {
      const transcript = (event as { transcript?: string }).transcript;
      if (transcript) {
        setMessages(prev => [...prev, { role: 'user', content: transcript }]);
      }
    }
  };

  const handleAutoEnd = (reason: 'maxDuration' | 'inactivity') => {
    setIsConnected(false);
    setIsSpeaking(false);
    setIsProcessingReservation(false);
    setReservationConfirmed(false);
    setStatus('ready');
    setMessages([]);
    if (processingTimeoutRef.current) clearTimeout(processingTimeoutRef.current);

    toast({
      title: "Call Ended",
      description:
        reason === 'inactivity'
          ? "The call ended after 30 seconds of inactivity."
          : "The call reached the maximum duration. Thank you for contacting us!",
    });
  };

  const startConversation = async () => {
    setIsConnecting(true);
    setStatus('connecting');

    try {
      // If there's an existing session for any reason, end it and start fresh.
      await chatRef.current?.disconnect();
      chatRef.current = new RealtimeChat(handleMessage, handleAutoEnd);
      await chatRef.current.init();
      setIsConnected(true);
      setStatus('listening');

      toast({
        title: "Connected",
        description: "Voice interface is ready. Start speaking!",
      });
    } catch (error) {
      console.error('Error starting conversation:', error);
      setStatus('ready');
      toast({
        title: "Connection Failed",
        description: error instanceof Error ? error.message : 'Failed to start conversation',
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const endConversation = async () => {
    await chatRef.current?.disconnect();
    setIsConnected(false);
    setIsSpeaking(false);
    setIsProcessingReservation(false);
    setReservationConfirmed(false);
    setStatus('ready');
    setMessages([]);
    if (processingTimeoutRef.current) clearTimeout(processingTimeoutRef.current);

    toast({
      title: "Disconnected",
      description: "Conversation ended",
    });
  };

  useEffect(() => {
    return () => {
      chatRef.current?.disconnect();
    };
  }, []);

  const statusText: Record<string, string> = {
    ready: 'Ready',
    connecting: 'Connecting...',
    listening: 'Listening',
    speaking: 'Speaking',
    processing: 'Booking reservation...',
  };

  return (
    <div className="flex flex-col items-center gap-8">
      {/* Processing Reservation Indicator */}
      {isProcessingReservation && (
        <div className="flex items-center gap-3 px-4 py-3 bg-primary/10 border border-primary/30 rounded-lg animate-pulse">
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
          <span className="text-sm font-medium text-primary">Processing your reservation...</span>
        </div>
      )}
      
      {/* Reservation Confirmed Indicator */}
      {reservationConfirmed && (
        <div className="flex items-center gap-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <CalendarCheck className="h-5 w-5 text-green-500" />
          <span className="text-sm font-medium text-green-500">Reservation confirmed!</span>
        </div>
      )}

      {/* Status Indicator */}
      <div className="flex items-center gap-2">
        <div className={cn(
          "h-3 w-3 rounded-full",
          status === 'ready' && "bg-muted-foreground",
          status === 'connecting' && "bg-yellow-500 animate-pulse",
          status === 'listening' && "bg-primary animate-pulse",
          status === 'speaking' && "bg-primary",
          status === 'processing' && "bg-amber-500 animate-pulse"
        )} />
        <span className="text-sm font-medium text-muted-foreground">
          {statusText[status]}
        </span>
      </div>

      {/* Microphone Button */}
      <button
        onClick={isConnected ? endConversation : startConversation}
        disabled={isConnecting}
        className={cn(
          "relative flex h-32 w-32 items-center justify-center rounded-full transition-all duration-300",
          "bg-secondary border-2 border-border hover:border-primary",
          isConnected && "bg-primary/20 border-primary",
          isSpeaking && "animate-pulse-glow",
          isConnecting && "opacity-50 cursor-not-allowed"
        )}
      >
        <div className={cn(
          "flex h-24 w-24 items-center justify-center rounded-full transition-all",
          isConnected ? "bg-primary" : "bg-secondary"
        )}>
          {isConnected ? (
            <PhoneOff className="h-10 w-10 text-primary-foreground" />
          ) : (
            <Phone className="h-10 w-10 text-muted-foreground" />
          )}
        </div>
        
        {/* Pulsing rings when connected */}
        {isConnected && (
          <>
            <div className="absolute inset-0 rounded-full border-2 border-primary animate-ping opacity-20" />
            <div className="absolute inset-[-8px] rounded-full border border-primary/30 animate-pulse" />
          </>
        )}
      </button>

      {/* Audio Visualizer */}
      {isConnected && (
        <div className="flex items-end gap-1 h-12">
          {[...Array(7)].map((_, i) => (
            <div
              key={i}
              className={cn(
                "w-2 rounded-full bg-primary transition-all duration-100",
                isSpeaking ? "animate-audio-wave" : "h-2"
              )}
              style={{
                animationDelay: `${i * 0.1}s`,
                height: isSpeaking ? `${Math.random() * 40 + 8}px` : '8px',
              }}
            />
          ))}
        </div>
      )}

      {/* Call to action */}
      <p className="text-sm text-muted-foreground text-center max-w-md">
        {isConnected 
          ? "Speak naturally to make a reservation or ask about our menu and hours."
          : "Click to start a conversation with our AI receptionist."
        }
      </p>

      {/* Conversation Transcript */}
      {messages.length > 0 && (
        <div className="w-full max-w-2xl mt-8">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Conversation</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto rounded-lg bg-card p-4 border border-border">
            {messages.map((message, index) => (
              <div
                key={index}
                className={cn(
                  "flex gap-2",
                  message.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                <div
                  className={cn(
                    "rounded-lg px-4 py-2 max-w-[80%]",
                    message.role === 'user'
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground"
                  )}
                >
                  <p className="text-sm">{message.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
